import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, ButtonGroup, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import axiosInstance from "../services/axiosInterceptor";
import { getToken, getUserId } from "../services/authorize";
import ThaiDateInput from '../components/ThaiDateInput'
import { toISODate } from '../utils/thaiDate'

const copyStatusLabel = {
  processing: "กำลังดำเนินการ",
  available: "พร้อมให้ยืม",
  on_loan: "กำลังถูกยืม",
  reserved: "ถูกจอง",
  repair: "ซ่อมแซม",
  lost: "สูญหาย",
  withdrawn: "ถอนออกจากระบบ"
};

const copyStatusVariant = {
  processing: "secondary",
  available: "success",
  on_loan: "warning",
  reserved: "info",
  repair: "danger",
  lost: "dark",
  withdrawn: "secondary"
};

const withdrawalTypeOptions = [
  { value: "other", label: "อื่นๆ" },
  { value: "damaged", label: "ชำรุด" },
  { value: "lost", label: "สูญหาย" },
  { value: "obsolete", label: "ล้าสมัย" },
  { value: "discarded", label: "คัดออก" }
];

const statusOptions = [
  "processing",
  "available",
  "reserved",
  "repair",
  "lost"
];

const toDateInputValue = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return toISODate(d);
};

const emptyTitleForm = {
  title_id: "",
  title_code: "",
  isbn: "",
  title_name: "",
  author_name: "",
  edition_no: "",
  call_number: "",
  publish_year: "",
  category_id: "",
  book_cover: "",
  title_status: "active"
};

const emptyCopyForm = {
  copy_id: "",
  copy_code: "",
  title_name: "",
  location_id: "",
  shelf_code: "",
  condition_status: "normal",
  is_reference: false,
  acq_price: "",
  acq_date: "",
  copy_note: "",
  copy_status: "available",
  status_reason: "",
  withdrawal_type: "other",
  withdrawal_reason: ""
};

export default function LibraryBookManagement() {
  const [masterData, setMasterData] = useState({ categories: [], locations: [] });
  const [titles, setTitles] = useState([]);
  const [copies, setCopies] = useState([]);
  const [shelves, setShelves] = useState([]);
  const [loading, setLoading] = useState(true);

  const [titleForm, setTitleForm] = useState(emptyTitleForm);
  const [yearEra, setYearEra] = useState('CE');
  const [copyForm, setCopyForm] = useState(emptyCopyForm);

  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState("");
  const [uploadingCover, setUploadingCover] = useState(false);

  const [keyword, setKeyword] = useState("");
  const [copyStatusFilter, setCopyStatusFilter] = useState("");

  const [savingTitle, setSavingTitle] = useState(false);
  const [savingCopy, setSavingCopy] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [withdrawingCopy, setWithdrawingCopy] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const headers = useMemo(() => ({ authorization: `Bearer ${getToken()}` }), []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const [masterRes, titlesRes, copiesRes, shelvesRes] = await Promise.all([
        axiosInstance.get(`${process.env.REACT_APP_API}/library/getMasterData`, { headers }),
        axiosInstance.get(`${process.env.REACT_APP_API}/library/getTitles`, { headers }),
        axiosInstance.get(`${process.env.REACT_APP_API}/library/getCopies`, { headers }),
        axiosInstance.get(`${process.env.REACT_APP_API}/library/getShelves`, { headers })
      ]);

      setMasterData(masterRes.data || { categories: [], locations: [] });
      setTitles(titlesRes.data || []);
      setCopies(copiesRes.data || []);
      setShelves(shelvesRes.data || []);
    } catch (fetchError) {
      setError("ไม่สามารถโหลดข้อมูลจัดการหนังสือได้");
      console.error("LibraryBookManagement fetch error:", fetchError);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredCopies = useMemo(() => {
    const lowerKeyword = keyword.trim().toLowerCase();

    return copies.filter((copy) => {
      if (copyStatusFilter && copy.copy_status !== copyStatusFilter) {
        return false;
      }

      if (!lowerKeyword) return true;
      return (
        String(copy.copy_code || "").toLowerCase().includes(lowerKeyword) ||
        String(copy.title_name || "").toLowerCase().includes(lowerKeyword) ||
        String(copy.loc_name || "").toLowerCase().includes(lowerKeyword) ||
        String(copy.shelf_code || "").toLowerCase().includes(lowerKeyword)
      );
    });
  }, [copies, keyword, copyStatusFilter]);

  const filteredShelvesByLocation = useMemo(() => {
    if (!copyForm.location_id) return [];

    return shelves
      .filter(
        (shelf) =>
          Number(shelf.tb_library_location_loc_id) === Number(copyForm.location_id) &&
          shelf.shelf_status === "active"
      )
      .map((shelf) => ({
        value: shelf.shelf_code,
        label: `${shelf.shelf_code}${shelf.shelf_name ? ` - ${shelf.shelf_name}` : ""}`
      }));
  }, [shelves, copyForm.location_id]);

  const handlePickTitle = (title) => {
    setTitleForm({
      title_id: String(title.title_id || ""),
      title_code: title.title_code || "",
      isbn: title.isbn || "",
      title_name: title.title_name || "",
      author_name: title.author_name || "",
      edition_no: title.edition_no || "",
      call_number: title.call_number || "",
      publish_year: title.publish_year || "",
      category_id: String(title.tb_library_category_category_id || title.category_id || ""),
      book_cover: title.book_cover || "",
      title_status: title.title_status || "active"
    });
    setCoverFile(null);
    setCoverPreview(
      title.book_cover
        ? title.book_cover.startsWith('http')
          ? title.book_cover
          : `${process.env.REACT_APP_API?.replace(/\/api$/, '') || ''}${title.book_cover}`
        : ""
    );
  };

  const handlePickCopy = (copy) => {
    setCopyForm({
      copy_id: String(copy.copy_id || ""),
      copy_code: copy.copy_code || "",
      title_name: copy.title_name || "",
      location_id: String(copy.location_id || ""),
      shelf_code: copy.shelf_code || "",
      condition_status: copy.condition_status || "normal",
      is_reference: Boolean(copy.is_reference),
      acq_price: copy.acq_price != null ? String(copy.acq_price) : "",
      acq_date: toDateInputValue(copy.acq_date),
      copy_note: copy.copy_note || "",
      copy_status: copy.copy_status || "available",
      status_reason: "",
      withdrawal_type: "other",
      withdrawal_reason: ""
    });
  };

  const clearMessage = () => {
    setError("");
    setSuccess("");
  };

  const handleSaveTitle = async (event) => {
    setLoading(true);
    event.preventDefault();
    clearMessage();

    const t_id = getUserId();
    if (!t_id) {
      setError("ไม่พบข้อมูลผู้ใช้งาน กรุณาเข้าสู่ระบบใหม่");
      return;
    }

    if (!titleForm.title_id || !titleForm.title_name.trim() || !titleForm.category_id) {
      setError("กรุณาเลือกชื่อหนังสือและกรอกข้อมูลให้ครบ");
      return;
    }

    try {
      setSavingTitle(true);
      let coverPath = titleForm.book_cover || null;
      if (coverFile) {
        try {
          coverPath = await uploadCoverFile();
        } catch (uploadError) {
          setError("อัพโหลดรูปภาพหน้าปกไม่สำเร็จ");
          return;
        }
      }

      await axiosInstance.post(
        `${process.env.REACT_APP_API}/library/updateTitle`,
        {
          title_id: Number(titleForm.title_id),
          isbn: titleForm.isbn.trim() || null,
          title_name: titleForm.title_name.trim(),
          author_name: titleForm.author_name.trim() || null,
          edition_no: titleForm.edition_no.trim() || null,
          call_number: titleForm.call_number.trim() || null,
          publish_year: titleForm.publish_year || null,
          book_cover: coverPath || null,
          category_id: Number(titleForm.category_id),
          title_status: titleForm.title_status,
          t_id
        },
        { headers }
      );

      setSuccess("บันทึกข้อมูลชื่อหนังสือสำเร็จ");
      await fetchData();
    } catch (saveError) {
      const message = saveError?.response?.data?.message;
      if (message === "duplicate isbn") {
        setError("ISBN ซ้ำกับข้อมูลในระบบ");
      } else if (message === "category not found") {
        setError("ไม่พบหมวดหมู่ที่เลือก");
      } else {
        setError("บันทึกข้อมูลชื่อหนังสือไม่สำเร็จ");
      }
      console.error("Save title error:", saveError);
    } finally {
      setSavingTitle(false);
      setLoading(false);
    }
  };

  const handleSaveCopy = async (event) => {
    event.preventDefault();
    clearMessage();

    const t_id = getUserId();
    if (!t_id) {
      setError("ไม่พบข้อมูลผู้ใช้งาน กรุณาเข้าสู่ระบบใหม่");
      return;
    }

    if (!copyForm.copy_id) {
      setError("กรุณาเลือกรายการเล่มที่ต้องการแก้ไข");
      return;
    }

    try {
      setSavingCopy(true);
      await axiosInstance.post(
        `${process.env.REACT_APP_API}/library/updateCopy`,
        {
          copy_id: Number(copyForm.copy_id),
          location_id: copyForm.location_id ? Number(copyForm.location_id) : null,
          shelf_code: copyForm.shelf_code || null,
          condition_status: copyForm.condition_status,
          is_reference: copyForm.is_reference,
          acq_price: copyForm.acq_price || 0,
          acq_date: copyForm.acq_date || null,
          copy_note: copyForm.copy_note.trim() || null,
          t_id
        },
        { headers }
      );

      setSuccess("บันทึกข้อมูลเล่มสำเร็จ");
      await fetchData();
    } catch (saveError) {
      const message = saveError?.response?.data?.message;
      if (message === "location not found") {
        setError("ไม่พบตำแหน่งจัดเก็บที่เลือก");
      } else if (message === "shelf not found") {
        setError("ไม่พบรหัสชั้นวางที่เลือก");
      } else {
        setError("บันทึกข้อมูลเล่มไม่สำเร็จ");
      }
      console.error("Save copy error:", saveError);
    } finally {
      setSavingCopy(false);
    }
  };

  const handleChangeCopyStatus = async () => {
    clearMessage();

    const t_id = getUserId();
    if (!t_id) {
      setError("ไม่พบข้อมูลผู้ใช้งาน กรุณาเข้าสู่ระบบใหม่");
      return;
    }

    if (!copyForm.copy_id || !copyForm.copy_status) {
      setError("กรุณาเลือกรายการเล่มและสถานะที่ต้องการเปลี่ยน");
      return;
    }

    try {
      setSavingStatus(true);
      await axiosInstance.post(
        `${process.env.REACT_APP_API}/library/changeCopyStatus`,
        {
          copy_id: Number(copyForm.copy_id),
          copy_status: copyForm.copy_status,
          reason: copyForm.status_reason.trim() || null,
          t_id
        },
        { headers }
      );

      setSuccess("เปลี่ยนสถานะเล่มสำเร็จ");
      await fetchData();
    } catch (statusError) {
      const message = statusError?.response?.data?.message;
      if (
        message === "invalid copy status transition" ||
        message === "on loan status must use borrow flow" ||
        message === "cannot change withdrawn copy status"
      ) {
        setError("ไม่สามารถเปลี่ยนสถานะตามเงื่อนไขปัจจุบันได้");
      } else {
        setError("เปลี่ยนสถานะเล่มไม่สำเร็จ");
      }
      console.error("Change copy status error:", statusError);
    } finally {
      setSavingStatus(false);
    }
  };

  const handleWithdrawCopy = async () => {
    clearMessage();

    const t_id = getUserId();
    if (!t_id) {
      setError("ไม่พบข้อมูลผู้ใช้งาน กรุณาเข้าสู่ระบบใหม่");
      return;
    }

    if (!copyForm.copy_id) {
      setError("กรุณาเลือกรายการเล่มที่ต้องการตัดจำหน่าย");
      return;
    }

    if (!window.confirm(`ยืนยันตัดจำหน่ายเล่ม ${copyForm.copy_code} ?`)) {
      return;
    }

    try {
      setWithdrawingCopy(true);
      await axiosInstance.post(
        `${process.env.REACT_APP_API}/library/withdrawCopy`,
        {
          copy_id: Number(copyForm.copy_id),
          withdrawal_type: copyForm.withdrawal_type,
          withdrawal_reason: copyForm.withdrawal_reason.trim() || null,
          t_id
        },
        { headers }
      );

      setSuccess("ตัดจำหน่ายเล่มสำเร็จ");
      await fetchData();
    } catch (withdrawError) {
      const message = withdrawError?.response?.data?.message;
      if (message === "cannot withdraw on loan copy") {
        setError("ไม่สามารถตัดจำหน่ายเล่มที่กำลังถูกยืมได้");
      } else if (message === "copy already withdrawn") {
        setError("เล่มนี้ถูกตัดจำหน่ายไปแล้ว");
      } else {
        setError("ตัดจำหน่ายเล่มไม่สำเร็จ");
      }
      console.error("Withdraw copy error:", withdrawError);
    } finally {
      setWithdrawingCopy(false);
    }
  };

  const handleCoverFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCoverFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setCoverPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const uploadCoverFile = async () => {
    if (!coverFile) return titleForm.book_cover || null;

    setUploadingCover(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append('coverImage', coverFile);

      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API}/library/uploadCover`,
        formData,
        {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const coverPath = response.data.coverPath;
      setTitleForm((prev) => ({ ...prev, book_cover: coverPath }));
      return coverPath;
    } catch (uploadError) {
      console.error("Cover upload error:", uploadError);
      throw uploadError;
    } finally {
      setUploadingCover(false);
    }
  };

  const handleRemoveCover = async () => {
    setError("");
    setSuccess("");
    setCoverFile(null);
    setCoverPreview("");
    setTitleForm((prev) => ({ ...prev, book_cover: "" }));

    if (!titleForm.title_id) {
      return;
    }

    const t_id = getUserId();
    if (!t_id) {
      setError("ไม่พบข้อมูลผู้ใช้งาน กรุณาเข้าสู่ระบบใหม่");
      return;
    }

    try {
      setUploadingCover(true);
      await axiosInstance.post(
        `${process.env.REACT_APP_API}/library/updateTitle`,
        {
          title_id: Number(titleForm.title_id),
          isbn: titleForm.isbn.trim() || null,
          title_name: titleForm.title_name.trim(),
          author_name: titleForm.author_name.trim() || null,
          edition_no: titleForm.edition_no.trim() || null,
          call_number: titleForm.call_number.trim() || null,
          publish_year: titleForm.publish_year || null,
          book_cover: null,
          category_id: Number(titleForm.category_id),
          title_status: titleForm.title_status,
          t_id
        },
        { headers }
      );

      setSuccess("ลบรูปภาพหน้าปกสำเร็จ");
      await fetchData();
    } catch (removeError) {
      setError("ไม่สามารถลบรูปภาพหน้าปกได้");
      console.error("Remove cover error:", removeError);
    } finally {
      setUploadingCover(false);
    }
  };

  return (
    <React.Fragment>
      <Header />
      <main className="main main-app p-3 p-lg-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h4 className="mb-0">จัดการข้อมูลหนังสือและเล่ม</h4>
          <Badge bg="secondary">Book Management</Badge>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Row className="g-3 mb-3">
          <Col lg={5}>
            <Card className="mb-3">
              <Card.Header className="fw-semibold">แก้ไขข้อมูลชื่อหนังสือ</Card.Header>
              <Card.Body>
                <Form onSubmit={handleSaveTitle}>
                  <Form.Group className="mb-2">
                    <Form.Label>รหัสชื่อหนังสือ</Form.Label>
                    <Form.Control value={titleForm.title_code} disabled />
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label>ชื่อหนังสือ</Form.Label>
                    <Form.Control
                      value={titleForm.title_name}
                      onChange={(event) => setTitleForm((prev) => ({ ...prev, title_name: event.target.value }))}
                    />
                  </Form.Group>

                  <Row className="g-2">
                    <Col md={8}>
                      <Form.Group className="mb-2">
                        <Form.Label>ชื่อผู้แต่ง</Form.Label>
                        <Form.Control
                          value={titleForm.author_name}
                          onChange={(event) => setTitleForm((prev) => ({ ...prev, author_name: event.target.value }))}
                          placeholder="เช่น สมชาย ใจดี"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-2">
                        <Form.Label>พิมพ์ครั้งที่</Form.Label>
                        <Form.Control
                          value={titleForm.edition_no}
                          onChange={(event) => setTitleForm((prev) => ({ ...prev, edition_no: event.target.value }))}
                          placeholder="เช่น พิมพ์ครั้งที่ 3"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="g-2">
                    <Col md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label>ISBN</Form.Label>
                        <Form.Control
                          value={titleForm.isbn}
                          onChange={(event) => setTitleForm((prev) => ({ ...prev, isbn: event.target.value }))}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-2">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <Form.Label className="mb-0">ปีพิมพ์</Form.Label>
                          <ButtonGroup size="sm">
                            <Button
                              variant={yearEra === 'CE' ? 'primary' : 'outline-primary'}
                              onClick={() => setYearEra('CE')}
                            >ค.ศ.</Button>
                            <Button
                              variant={yearEra === 'BE' ? 'primary' : 'outline-primary'}
                              onClick={() => setYearEra('BE')}
                            >พ.ศ.</Button>
                          </ButtonGroup>
                        </div>
                        <Form.Control
                          type="number"
                          min={yearEra === 'BE' ? 2443 : 1900}
                          max={yearEra === 'BE' ? 2643 : 2100}
                          placeholder={yearEra === 'BE' ? 'เช่น 2567' : 'เช่น 2024'}
                          value={
                            titleForm.publish_year === '' ? '' :
                            yearEra === 'BE' ? Number(titleForm.publish_year) + 543 : titleForm.publish_year
                          }
                          onChange={(event) => {
                            const raw = event.target.value;
                            const stored = raw === '' ? '' : yearEra === 'BE' ? Number(raw) - 543 : raw;
                            setTitleForm((prev) => ({ ...prev, publish_year: stored }));
                          }}
                        />
                        <Form.Text className="text-muted">
                          {titleForm.publish_year !== '' && (
                            yearEra === 'BE'
                              ? `ค.ศ. ${titleForm.publish_year}`
                              : `พ.ศ. ${Number(titleForm.publish_year) + 543}`
                          )}
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-2">
                    <Form.Label>เลขหมู่</Form.Label>
                    <Form.Control
                      value={titleForm.call_number}
                      onChange={(event) => setTitleForm((prev) => ({ ...prev, call_number: event.target.value }))}
                    />
                  </Form.Group>

                  <Row className="g-2">
                    <Col md={8}>
                      <Form.Group className="mb-2">
                        <Form.Label>หมวดหมู่</Form.Label>
                        <Form.Select
                          value={titleForm.category_id}
                          onChange={(event) => setTitleForm((prev) => ({ ...prev, category_id: event.target.value }))}
                        >
                          <option value="">เลือกหมวดหมู่</option>
                          {(masterData.categories || []).map((category) => (
                            <option key={category.category_id} value={category.category_id}>
                              {category.category_name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-2">
                        <Form.Label>สถานะ</Form.Label>
                        <Form.Select
                          value={titleForm.title_status}
                          onChange={(event) => setTitleForm((prev) => ({ ...prev, title_status: event.target.value }))}
                        >
                          <option value="active">ใช้งาน</option>
                          <option value="inactive">ปิดใช้งาน</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>รูปภาพหน้าปก</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={handleCoverFileChange}
                      disabled={!titleForm.title_id}
                    />
                    {coverPreview && (
                      <div className="mt-2">
                        <img
                          src={coverPreview}
                          alt="Cover preview"
                          style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain' }}
                          className="border rounded"
                        />
                      </div>
                    )}
                    <div className="mt-2">
                      <Button
                        type="button"
                        variant="outline-danger"
                        size="sm"
                        onClick={handleRemoveCover}
                        disabled={!coverPreview || !titleForm.title_id}
                      >
                        ลบรูปภาพ
                      </Button>
                    </div>
                  </Form.Group>

                  <Button type="submit" disabled={savingTitle || !titleForm.title_id}>
                    {savingTitle ? "กำลังบันทึก..." : "บันทึกชื่อหนังสือ"}
                  </Button>
                </Form>
              </Card.Body>
            </Card>

            <Card>
              <Card.Header className="fw-semibold">แก้ไขข้อมูลเล่ม / สถานะ / ตัดจำหน่าย</Card.Header>
              <Card.Body>
                <Form onSubmit={handleSaveCopy}>
                  <Form.Group className="mb-2">
                    <Form.Label>รหัสเล่ม</Form.Label>
                    <Form.Control value={copyForm.copy_code} disabled />
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label>ชื่อหนังสือ</Form.Label>
                    <Form.Control value={copyForm.title_name} disabled />
                  </Form.Group>

                  <Row className="g-2">
                    <Col md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label>ตำแหน่งจัดเก็บ</Form.Label>
                        <Form.Select
                          value={copyForm.location_id}
                          onChange={(event) =>
                            setCopyForm((prev) => ({
                              ...prev,
                              location_id: event.target.value,
                              shelf_code: ""
                            }))
                          }
                        >
                          <option value="">ไม่ระบุ</option>
                          {(masterData.locations || []).map((location) => (
                            <option key={location.loc_id} value={location.loc_id}>
                              {location.loc_code} - {location.loc_name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label>รหัสชั้นวาง</Form.Label>
                        <Form.Select
                          value={copyForm.shelf_code}
                          disabled={!copyForm.location_id}
                          onChange={(event) => setCopyForm((prev) => ({ ...prev, shelf_code: event.target.value }))}
                        >
                          <option value="">
                            {!copyForm.location_id ? "เลือกตำแหน่งจัดเก็บก่อน" : "เลือกรหัสชั้นวาง"}
                          </option>
                          {filteredShelvesByLocation.map((shelf) => (
                            <option key={shelf.value} value={shelf.value}>
                              {shelf.label}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="g-2">
                    <Col md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label>สภาพเล่ม</Form.Label>
                        <Form.Select
                          value={copyForm.condition_status}
                          onChange={(event) => setCopyForm((prev) => ({ ...prev, condition_status: event.target.value }))}
                        >
                          <option value="normal">ปกติ</option>
                          <option value="damaged">ชำรุด</option>
                          <option value="repairing">กำลังซ่อม</option>
                          <option value="lost">สูญหาย</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label>ราคาได้มา</Form.Label>
                        <Form.Control
                          type="number"
                          min={0}
                          step="0.01"
                          value={copyForm.acq_price}
                          onChange={(event) => setCopyForm((prev) => ({ ...prev, acq_price: event.target.value }))}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-2">
                    <Form.Label>วันที่รับเข้า</Form.Label>
                    <ThaiDateInput
                      value={copyForm.acq_date}
                      onChange={(event) => setCopyForm((prev) => ({ ...prev, acq_date: event.target.value }))}
                    />
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Check
                      type="checkbox"
                      label="เป็นหนังสืออ้างอิง"
                      checked={copyForm.is_reference}
                      onChange={(event) => setCopyForm((prev) => ({ ...prev, is_reference: event.target.checked }))}
                    />
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label>หมายเหตุเล่ม</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={copyForm.copy_note}
                      onChange={(event) => setCopyForm((prev) => ({ ...prev, copy_note: event.target.value }))}
                    />
                  </Form.Group>

                  <Button type="submit" disabled={savingCopy || !copyForm.copy_id}>
                    {savingCopy ? "กำลังบันทึก..." : "บันทึกข้อมูลเล่ม"}
                  </Button>
                </Form>

                <hr />

                <Form.Group className="mb-2">
                  <Form.Label>เปลี่ยนสถานะเล่ม</Form.Label>
                  <Form.Select
                    value={copyForm.copy_status}
                    onChange={(event) => setCopyForm((prev) => ({ ...prev, copy_status: event.target.value }))}
                    disabled={!copyForm.copy_id}
                  >
                    {statusOptions.map((status) => (
                      <option key={status} value={status}>
                        {copyStatusLabel[status] || status}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-2">
                  <Form.Label>เหตุผลเปลี่ยนสถานะ</Form.Label>
                  <Form.Control
                    value={copyForm.status_reason}
                    onChange={(event) => setCopyForm((prev) => ({ ...prev, status_reason: event.target.value }))}
                    disabled={!copyForm.copy_id}
                  />
                </Form.Group>

                <Button
                  variant="outline-primary"
                  className="me-2"
                  onClick={handleChangeCopyStatus}
                  disabled={savingStatus || !copyForm.copy_id}
                >
                  {savingStatus ? "กำลังเปลี่ยนสถานะ..." : "บันทึกสถานะเล่ม"}
                </Button>

                <hr />

                <Form.Group className="mb-2">
                  <Form.Label>ประเภทการตัดจำหน่าย</Form.Label>
                  <Form.Select
                    value={copyForm.withdrawal_type}
                    onChange={(event) => setCopyForm((prev) => ({ ...prev, withdrawal_type: event.target.value }))}
                    disabled={!copyForm.copy_id}
                  >
                    {withdrawalTypeOptions.map((typeOption) => (
                      <option key={typeOption.value} value={typeOption.value}>
                        {typeOption.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-2">
                  <Form.Label>เหตุผลตัดจำหน่าย</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={copyForm.withdrawal_reason}
                    onChange={(event) =>
                      setCopyForm((prev) => ({ ...prev, withdrawal_reason: event.target.value }))
                    }
                    disabled={!copyForm.copy_id}
                  />
                </Form.Group>

                <Button
                  variant="danger"
                  onClick={handleWithdrawCopy}
                  disabled={withdrawingCopy || !copyForm.copy_id}
                >
                  {withdrawingCopy ? "กำลังตัดจำหน่าย..." : "ตัดจำหน่ายเล่ม"}
                </Button>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={7}>
            <Card className="mb-3">
              <Card.Header className="fw-semibold">เลือกชื่อหนังสือเพื่อแก้ไข</Card.Header>
              <Card.Body className="p-0">
                <Table responsive hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>รหัส</th>
                      <th>ชื่อหนังสือ</th>
                      <th>หมวดหมู่</th>
                      <th>สถานะ</th>
                      <th>จัดการ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!loading && titles.length === 0 && (
                      <tr>
                        <td colSpan={5} className="text-center text-muted py-3">
                          ไม่พบข้อมูลชื่อหนังสือ
                        </td>
                      </tr>
                    )}
                    {titles.slice(0, 50).map((title) => (
                      <tr key={title.title_id}>
                        <td>{title.title_code}</td>
                        <td>{title.title_name}</td>
                        <td>{title.category_name || "-"}</td>
                        <td>
                          <Badge bg={title.title_status === "active" ? "success" : "secondary"}>
                            {title.title_status === "active" ? "ใช้งาน" : "ปิดใช้งาน"}
                          </Badge>
                        </td>
                        <td>
                          <Button size="sm" variant="outline-primary" onClick={() => handlePickTitle(title)}>
                            เลือกแก้ไข
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>

            <Card>
              <Card.Header className="fw-semibold d-flex align-items-center justify-content-between">
                <span>รายการเล่มหนังสือ</span>
                {loading && <Spinner animation="border" size="sm" />}
              </Card.Header>
              <Card.Body>
                <Row className="g-2 mb-2">
                  <Col md={8}>
                    <Form.Control
                      placeholder="ค้นหา รหัสเล่ม / ชื่อหนังสือ / ตำแหน่ง / ชั้นวาง"
                      value={keyword}
                      onChange={(event) => setKeyword(event.target.value)}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Select value={copyStatusFilter} onChange={(event) => setCopyStatusFilter(event.target.value)}>
                      <option value="">ทุกสถานะ</option>
                      {Object.keys(copyStatusLabel).map((status) => (
                        <option key={status} value={status}>
                          {copyStatusLabel[status]}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                </Row>

                <div className="border rounded" style={{ maxHeight: 520, overflowY: "auto" }}>
                  <Table responsive hover className="mb-0">
                    <thead className="table-secondary sticky-top">
                      <tr>
                        <th>รหัสเล่ม</th>
                        <th>ชื่อหนังสือ</th>
                        <th>ตำแหน่ง</th>
                        <th>ชั้นวาง</th>
                        <th>สถานะ</th>
                        <th>จัดการ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!loading && filteredCopies.length === 0 && (
                        <tr>
                          <td colSpan={6} className="text-center text-muted py-3">
                            ไม่พบข้อมูลเล่มหนังสือ
                          </td>
                        </tr>
                      )}
                      {filteredCopies.slice(0, 300).map((copy) => (
                        <tr key={copy.copy_id}>
                          <td>{copy.copy_code}</td>
                          <td>{copy.title_name}</td>
                          <td>{copy.loc_name || "-"}</td>
                          <td>{copy.shelf_code || "-"}</td>
                          <td>
                            <Badge bg={copyStatusVariant[copy.copy_status] || "secondary"}>
                              {copyStatusLabel[copy.copy_status] || copy.copy_status}
                            </Badge>
                          </td>
                          <td>
                            <Button size="sm" variant="outline-primary" onClick={() => handlePickCopy(copy)}>
                              เลือกแก้ไข
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </main>
      <Footer />
    </React.Fragment>
  );
}
